// src/components/MenuDrawer.js
import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { apiRequest } from "../services/api";
import { getPublicCategories } from "../services/categories.api";

function MenuDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const { cartItems } = useCart();

  const [activeTab, setActiveTab] = useState(null);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [topCategories, setTopCategories] = useState([]);

  const [allCategories, setAllCategories] = useState([]);
  const [showCategories, setShowCategories] = useState(false);

  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);

  /* ================= LOAD CATEGORIES ================= */
  useEffect(() => {
    if (!isOpen) return;
    getPublicCategories().then(setAllCategories).catch(() => {});
  }, [isOpen]);

  /* ================= LOAD PRODUCTS ================= */
  useEffect(() => {
    let mounted = true;

    async function load() {
      const res = await getAllProducts();
      const products = res.map(mapBackendProduct);

      const grouped = {};
      products.forEach((p) => {
        if (!grouped[p.category]) grouped[p.category] = [];
        grouped[p.category].push(p);
      });

      const sorted = Object.entries(grouped)
        .sort((a, b) => b[1].length - a[1].length)
        .slice(0, 3)
        .map(([key, items]) => ({ key, products: items }));

      if (!mounted) return;

      setTopCategories(sorted);

      const data = {};
      sorted.forEach((c) => (data[c.key] = c.products));
      setProductsByCategory(data);
      setActiveTab(sorted[0]?.key || null);
    }

    if (isOpen) load();
    return () => (mounted = false);
  }, [isOpen]);

  /* ================= ANALYTICS ================= */
  useEffect(() => {
    if (!isOpen) return;
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "enter_menu_drawer",
        entity_type: "navigation",
        entity_id: null,
      }),
    }).catch(() => {});
  }, [isOpen]);

  /* ================= BODY LOCK ================= */
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [isOpen]);

  const categoryProducts = productsByCategory[activeTab] || [];
  const menuItems = useMemo(() => categoryProducts.slice(0, 3), [categoryProducts]);
  const imageItems = useMemo(() => categoryProducts.slice(0, 3), [categoryProducts]);

  /* ================= NAV HELPERS ================= */
  const openCategory = (section) => {
  onClose();

  if (["new-in", "sale", "bestsellers"].includes(section)) {
    navigate(`/products/${section}`);
  } else {
    navigate(`/products/category/${section}`);
  }
};

const openCategoryFromMenu = (slug) => {
  onClose();
  navigate(`/products/category/${slug}`);
};


  const openAllProducts = () => {
    onClose();
    navigate("/all-products");
  };

 const openSpecial = (type) => {
  onClose();
  navigate(`/products/${type}`);
};

const isRTL = lang === "AR";

const truncateText = (text = "", max = 40) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max - 3) + "...";
};


  return (
    <div
      className={`fixed inset-0 z-[9999] transition-opacity duration-300
      ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div
  className={`
    absolute top-0 left-0 w-full h-[90vh]
    bg-[#0F1A12] text-white
    transform transition-transform duration-300
    ${isOpen ? "translate-y-0" : "-translate-y-full"}
    md:w-[500px] md:h-screen md:translate-y-0
    ${isOpen ? "md:translate-x-0" : "md:-translate-x-full"}
  `}
  dir={lang === "AR" ? "rtl" : "ltr"}
>
        {/* ================= TOP BAR ================= */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#2E5A2A]/30">
          <div className="flex items-center gap-4 px-4 py-2 rounded-full border border-[#2E5A2A]/40 bg-black shadow-lg">
            <button onClick={() => {
              const token = localStorage.getItem("esill-token");
              navigate(token ? "/account" : "/create-account");
            }}>
              <img src="/icons/user-v2.png" className="w-8 h-8 invert"
  alt="Account" />
            </button>

            <button onClick={() => navigate("/wishlist")}>
              <img src="/icons/heart.png" className="w-7 h-7 invert" alt="Wishlist"/>
            </button>

            <button onClick={() => navigate("/cart")} className="relative">
              <img src="/icons/bag.png" className="w-7 h-7 invert" alt="Cart"/>
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
          </div>

          <button onClick={onClose} className="text-3xl">×</button>
        </div>

        {/* ================= TABS ================= */}
        <div className="flex justify-center gap-12 mt-4 border-b border-[#2E5A2A]/20 px-4 pb-2">
          {topCategories.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-2 text-base md:text-lg font-semibold relative
                  ${isActive ? "text-[#2E5A2A]" : "text-white/40"}`}
              >
                {tab.key.toUpperCase()}
                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-[1px] h-[3px] bg-[#2E5A2A]" />
                )}
              </button>
            );
          })}
        </div>

        {/* ================= CONTENT ================= */}
        <div className="h-[calc(90vh-150px)] overflow-y-auto px-5 pt-5 pb-6">
          <div className="space-y-4 mb-6">
            <button
  onClick={() => openSpecial("new-in")}
  className={`block w-full text-base font-semibold py-1 hover:text-[#2E5A2A] transition
    ${isRTL ? "text-right" : "text-left"}
  `}
>
  <span className="inline-flex items-center">
    <span>{lang === "EN" ? "New Collection" : "مجموعة جديدة"}</span>

    <span
      className={`text-[10px] px-2 py-[2px] rounded bg-[#2E5A2A] text-white font-bold
        ${isRTL ? "mr-3" : "ml-3"}
      `}
    >
      {lang === "EN" ? "NEW" : "جديد"}
    </span>
  </span>
</button>

            <button onClick={() => openSpecial("bestsellers")}
  className={`block w-full text-base font-semibold py-1 hover:text-[#2E5A2A] transition
  ${isRTL ? "text-right" : "text-left"}
`}
>
              {lang === "EN" ? "Bestsellers" : "الأكثر مبيعًا"}
            </button>

            <button onClick={() => openSpecial("sale")} 
className={`block w-full text-base font-semibold py-1 hover:text-[#2E5A2A] transition
  ${isRTL ? "text-right" : "text-left"}
`}
>
              {lang === "EN" ? "On Sale" : "تخفيضات"}
            </button>

            <button
  onClick={openAllProducts}
  className={`block w-full text-base font-semibold py-1 hover:text-[#2E5A2A] transition
    ${isRTL ? "text-right" : "text-left"}
  `}
>
  <span className="inline-flex items-center">
    <span>{lang === "EN" ? "Explore All Products" : "تصفح كل المنتجات"}</span>

    <span
      className={`text-[10px] px-2 py-[2px] rounded bg-[#2E5A2A] text-white font-bold
        ${isRTL ? "mr-3" : "ml-3"}
      `}
    >
      {lang === "EN" ? "ALL" : "الكل"}
    </span>
  </span>
</button>



            {/* Categories dropdown */}
            <button
  onClick={() => setShowCategories((p) => !p)}
  className="block w-full text-left text-base font-semibold py-1 hover:text-[#2E5A2A] transition flex items-center justify-between"
>
  <span>{lang === "EN" ? "Categories" : "الفئات"}</span>
  <span className="ml-4 mr-4 text-sm opacity-70">
    {showCategories ? "▲" : "▼"}
  </span>
</button>
            {showCategories && (
  <div
  className={`
    mt-2
    ${isRTL ? "mr-3" : "ml-3"}
    rounded-xl
    bg-black/30
    border border-[#2E5A2A]/20
    divide-y divide-white/5
    overflow-hidden
  `}
>
    {allCategories.map((c) => (
      <button
        key={c.slug}
        onClick={() => openCategoryFromMenu(c.slug)}
        className={`
  w-full
  ${isRTL ? "text-right" : "text-left"}
  px-4 py-2
  text-sm
  text-white/70
  hover:text-[#2E5A2A]
  hover:bg-[#2E5A2A]/10
  transition
`}
      >
        {lang === "EN" ? c.name_en : c.name_ar}
      </button>
    ))}
  </div>
)}

          </div>

          {/* Product titles */}
          <div className="space-y-4 mb-10">
            {menuItems.map((p) => (
             <button
  key={p.id}
  onClick={() => openCategory(activeTab)}
  className={`w-full flex items-center text-sm md:text-base hover:text-[#2E5A2A]
    ${isRTL ? "flex-row-reverse text-right" : "flex-row text-left"}
  `}
>
  {/* TEXT */}
  <span className="flex-1">
    {truncateText(lang === "EN" ? p.nameEN : p.nameAR, 40)}
  </span>

  {/* ARROW */}
  <span
    className={`opacity-40
      ${isRTL ? "ml-2" : "ml-2"}
    `}
  >
    {isRTL ? "‹" : "›"}
  </span>
</button>


            ))}
          </div>

          {/* Images */}
          <div className="grid grid-cols-3 gap-3">
            {imageItems.map((p) => (
              <button
  key={p.id}
  onClick={() => openCategory(activeTab)}
  className="relative overflow-hidden bg-black group"
>
  <img
    src={p.images[0] || "/images/placeholder-product.jpg"}
    className="w-full h-40 object-cover group-hover:scale-110 transition"
    alt={p.nameEN}
  />

  {/* overlay */}
  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A12]/90 via-[#0F1A12]/40 to-transparent" />

  {/* text */}
  <span
  className={`
    absolute bottom-2
    ${isRTL ? "right-2 text-right" : "left-2 text-left"}
    ${isRTL ? "left-2" : "right-2"}
    text-xs font-semibold
    text-white
    bg-[#2E5A2A]/80
    px-2 py-1
    rounded
  `}
>
  {truncateText(lang === "EN" ? p.nameEN : p.nameAR, 20)}
</span>

</button>

            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MenuDrawer;
